import { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Hook to manage the global navigation state, including sidebar expansion
 * and active section detection.
 * 
 * @returns {Object} Navigation state and control functions
 */
const useNavigation = () => {
    const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
    const [isSidebarLocked, setIsSidebarLocked] = useState(() => {
        return localStorage.getItem('ox_sidebar_locked') === 'true';
    });
    const location = useLocation();
    const navigate = useNavigate();

    // Persist sidebar locked state
    useEffect(() => {
        localStorage.setItem('ox_sidebar_locked', isSidebarLocked);
    }, [isSidebarLocked]);

    // Reset expanded hover state on route change to prevent frozen hover states
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [location.pathname]);

    const toggleSidebar = useCallback(() => {
        setIsSidebarExpanded(prev => !prev);
    }, []);

    const toggleSidebarLock = useCallback(() => {
        setIsSidebarLocked(prev => !prev);
    }, []);

    const handleBack = useCallback(() => {
        navigate(-1);
    }, [navigate]);


    const closeSidebar = useCallback(() => {
        if (!isSidebarLocked) {
            setIsSidebarExpanded(false);
        }
    }, [isSidebarLocked]);

    /**
     * Determines the page title based on the current path
     * @returns {string} The formatted page title
     */
    const getPageTitle = useCallback(() => {
        const path = location.pathname;
        if (path.includes('/tools/leader')) return 'Kolonnenführer';
        if (path.includes('/tools/employee')) return 'Mitarbeiterverwaltung';
        if (path.includes('/tools/vehicleStatus')) return 'Fuhrpark & Fristen';
        if (path.includes('/tools/timesheet')) return 'Stundennachweis';
        if (path.includes('/tools/journal')) return 'Bautagebuch';
        if (path.includes('/tools/rental')) return 'Vermietung';
        if (path.includes('/tools/internalHandover')) return 'Interne Übergabe';
        if (path.includes('/verwaltung/mitarbeiter')) return 'Mitarbeiterverwaltung';
        if (path.includes('/verwaltung/fuhrpark')) return 'Fuhrpark und Fristen';
        if (path.includes('/verwaltung/projekte')) return 'Projektverwaltung';
        if (path.includes('/verwaltung/kunden')) return 'Kundenverwaltung';
        if (path.includes('/tools')) return 'Werkzeuge';
        if (path.includes('/verwaltung')) return 'Verwaltung';
        if (path === '/admin') return 'Hauptmenü';
        if (path.includes('/admin')) return 'Admin-Panel';
        
        return 'Hauptmenü';
    }, [location.pathname]);

    return {
        /** @type {boolean} True if the sidebar should be visually expanded (either by hover or lock) */
        isSidebarExpanded: isSidebarLocked || isSidebarExpanded,
        /** @type {boolean} True if the sidebar is pinned/sticky */
        isSidebarLocked,
        /** @type {Function} Toggle the temporary expansion (hover) */
        toggleSidebar,
        /** @type {Function} Toggle the persistent expansion (sticky) */
        toggleSidebarLock,
        /** @type {Function} Collapse the sidebar if not locked */
        closeSidebar,
        /** @type {Function} Get the localized title for the current route */
        getPageTitle,
        /** @type {Function} Global back navigation */
        handleBack,
        /** @type {string} Current normalized pathname */
        currentPath: location.pathname
    };
};

export default useNavigation;
